import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { NewsCard } from '../components/news/NewsCard';
import WeatherWidget from '../components/home/WeatherWidget';
import { MatchList } from '../components/matches/MatchList';
import StadiumFacts from '../components/home/StadiumFacts';
import type { NewsItem } from '../types';
import { fetchNews } from '../services/newsApi';
import { Building2, Trophy, Clock } from 'lucide-react';
import NextMatch from '../components/home/NextMatch';

export function Home() {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadNews = async () => {
      try {
        setLoading(true);
        const data = await fetchNews();
        setNews(data.slice(0, 3)); // Get only latest 3 news
      } catch (err) {
        console.error('Error loading news:', err);
        setError('Failed to load news');
      } finally {
        setLoading(false);
      }
    };

    loadNews();
  }, []);

  return (
    <>
      <Helmet>
        <title>Aker Stadium - Home of Molde FK | Unofficial Fansite</title>
        <meta 
          name="description" 
          content="Discover everything about Aker Stadium, home of Molde FK. Learn about the stadium's history, upcoming events, and experience the unique atmosphere by the fjord."
        />
      </Helmet>
      <div className="min-h-screen bg-gray-100">
        {/* Hero Section */}
        <div className="relative h-[70vh] bg-black">
          <img
            src="/images/molde-4.jpg"
            alt="Aker Stadion"
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-6 text-white">
              <div className="max-w-3xl">
                <div className="inline-block bg-[#2173B9] text-white text-sm font-medium px-3 py-1 rounded-full mb-4">
                  Unofficial Fansite
                </div>
                <h1 className="text-5xl font-bold mb-6">Aker Stadium</h1>
                <p className="text-xl opacity-90">
                  Discover everything about Aker Stadium, home of Molde FK, from 
                  its history to upcoming events and more.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 relative">
          {/* Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 -mt-32 mb-12 relative z-10">
            <WeatherWidget />
            <NextMatch />
            <StadiumFacts />
          </div>

          {/* Latest Results */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-12">
            <div className="flex items-center gap-2 mb-4">
              <Trophy className="w-5 h-5 text-[#2173B9]" />
              <h3 className="text-lg font-semibold">Latest Results</h3>
            </div>
            <MatchList type="last" limit={3} showLeague={false} />
          </div>

          {/* Stadium Info Sections */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <img 
                src="/images/molde-2.jpg"
                alt="Aker Stadium Overview"
                className="w-full h-48 object-cover rounded-lg mb-4"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/images/f-image-molde.png';
                }}
              />
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-5 h-5 text-[#2173B9]" />
                <h2 className="text-2xl font-bold">About Aker Stadium</h2>
              </div>
              <p className="text-gray-600">
                Aker Stadium is one of the most well-known football arenas in Norway, located in Molde. 
                With a capacity of around 11,200 spectators, it serves as the home ground of Molde FK. 
                The stadium is renowned for its spectacular location by the fjord and plays a vital role 
                in local football and cultural events.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6">
              <img 
                src="/images/molde-3.jpg"
                alt="Aker Stadium Historical View"
                className="w-full h-48 object-cover rounded-lg mb-4"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/images/f-image-molde.png';
                }}
              />
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-[#2173B9]" />
                <h2 className="text-2xl font-bold">History</h2>
              </div>
              <p className="text-gray-600">
                Opened in 1998, Aker Stadium has evolved into a modern football arena that meets 
                today's facility requirements. Over the years, the stadium has witnessed many exciting 
                matches and been part of the club's success in Norwegian football.
              </p>
            </div>
          </div>

          {/* Stadium Management */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-12">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-5 h-5 text-[#2173B9]" />
              <h2 className="text-2xl font-bold">Operations & Events</h2>
            </div>
            <p className="text-gray-600">
              Aker Stadium is owned and operated by Molde FK, which is responsible for both maintenance 
              and development of the facility. The stadium hosts football matches, concerts, and other 
              cultural events, serving as an important gathering point for Molde and the surrounding region.
            </p>
          </div>

          {/* Latest News */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6">Latest News</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {loading ? (
                [...Array(3)].map((_, i) => (
                  <div key={i} className="bg-white rounded-lg shadow-lg animate-pulse">
                    <div className="h-48 bg-gray-200 rounded-t-lg" />
                    <div className="p-6">
                      <div className="h-6 bg-gray-200 rounded mb-4" />
                      <div className="h-4 bg-gray-200 rounded mb-2" />
                      <div className="h-4 bg-gray-200 rounded w-2/3" />
                    </div>
                  </div>
                ))
              ) : error ? (
                <div className="col-span-3 text-center py-8 text-gray-500">
                  {error}
                </div>
              ) : news.length === 0 ? (
                <div className="col-span-3 text-center py-8 text-gray-500">
                  No news available
                </div>
              ) : (
                news.map((newsItem) => (
                  <NewsCard key={newsItem.id} news={newsItem} />
                ))
              )}
            </div>
          </div>

          {/* Video Section */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-12">
            <h2 className="text-2xl font-bold mb-6">Experience Aker Stadium</h2>
            <div className="aspect-w-16 aspect-h-9">
              <iframe
                src="https://www.youtube.com/embed/BEbK_ZI0qJA"
                title="Aker Stadium Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-[400px] rounded-lg"
              ></iframe>
            </div>
          </div>

          {/* Map Section */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d19719.371542494955!2d7.143110729066943!3d62.73317471038867!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4611540387baed8d%3A0x8f16e9f01e810845!2sAker%20Stadium!5e1!3m2!1sen!2sbg!4v1729850607616!5m2!1sen!2sbg"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Aker Stadium Location"
            ></iframe>
          </div>
        </div>
      </div>
    </>
  );
}
